import * as Sentry from "@sentry/react";
import React, {
  useContext,
  createContext,
  useEffect,
  useCallback,
  useState,
  useMemo,
  ReactElement,
} from "react";
import { CognitoIdentityCredentials } from "aws-sdk/global";
import {
  CognitoIdentityClient,
  GetCredentialsForIdentityCommand,
} from "@aws-sdk/client-cognito-identity";
import {
  CognitoUserPool,
  AuthenticationDetails,
  CognitoUser,
  CognitoUserSession,
  CognitoUserAttribute,
  UserData,
} from "amazon-cognito-identity-js";
import { promisify } from "./utils/promisify";
import {
  NotAuthorizedException,
  NotAuthorizedExceptionName,
  PasswordResetRequiredException,
  PasswordResetRequiredExceptionName,
  UserNotConfirmedException,
  UserNotConfirmedExceptionName,
} from "./AWSAuthErrors";
import { userAttributesToObject } from "./utils/cognitoHelpers";
import createSigV4Fetch from "./client/sigv4Fetch";

export type Credentials = CognitoIdentityCredentials;

export type AuthenticatedUser = {
  email: string;
  isTblAccount: boolean;
};

interface IUserAttributesObject {
  email: string;
  email_verified: boolean;
  sub: string;
}

export enum AuthState {
  Initializing = "initializing",
  Error = "error",
  Unauthenticated = "unauthenticated",
  Authenticated = "authenticated",
}

export type AuthContext = {
  state: AuthState;
  user: AuthenticatedUser;
  // Note that the IdentityID may be expired. currentCredentials should be used for anything which requires current credentials.
  identityId: string;
  isAuthenticated: boolean;
  authorizedFetch: typeof fetch;
  currentCredentials(): Promise<Credentials>;
  signIn(username: string, password: string);
  signOut(): void;
  signUp(username: string, password: string): void;
  verifyEmail(email: string, password: string, verificationCode: string): void;
  resendEmailVerification(email: string): void;
  sendPasswordReset(email: string): void;
  resetPassword(
    email: string,
    verificationCode: string,
    newPassword: string
  ): void;
  changePassword(oldPassword: string, newPassword: string): void;
};

const Context = createContext<AuthContext>({
  state: AuthState.Initializing,
} as AuthContext);

type AuthContextProviderProps = {
  config: AuthConfigOptions;
  children: ReactElement;
};

type AuthConfigOptions = {
  // REQUIRED only for Federated Authentication - Amazon Cognito Identity Pool ID
  identityPoolId: string;
  // REQUIRED - Amazon Cognito Region
  region: string;
  // OPTIONAL - Amazon Cognito User Pool ID
  userPoolId: string;
  // OPTIONAL - Amazon Cognito Web Client ID (26-char alphanumeric string)
  userPoolWebClientId: string;
};

export function AWSAuthContextProvider({
  config,
  children,
}: AuthContextProviderProps) {
  const [state, setState] = useState<AuthState>(AuthState.Initializing);
  const [user, setUser] = useState<AuthenticatedUser>();
  const [credentials, setCredentials] = useState<Credentials>();

  const userPool = useMemo(() => {
    return new CognitoUserPool({
      UserPoolId: config.userPoolId,
      ClientId: config.userPoolWebClientId,
    });
  }, []);

  const getCredentials = useMemo(() => {
    return function (userSession: CognitoUserSession = null) {
      const Logins = {};
      let IdentityId: string = undefined;
      if (userSession) {
        Logins[
          `cognito-idp.${config.region}.amazonaws.com/${config.userPoolId}`
        ] = userSession.getIdToken().getJwtToken();
      } else {
        IdentityId =
          window.localStorage.getItem(
            `aws.cognito.identity-id.${config.identityPoolId}`
          ) || undefined;
      }
      return new CognitoIdentityCredentials(
        {
          IdentityId,
          IdentityPoolId: config.identityPoolId,
          Logins,
        },
        { region: config.region }
      );
    };
  }, [config]);

  /**
   * Should always return the current non-expired Cognito identity credentials.
   */
  const currentCredentials = useCallback(async () => {
    if (!credentials) {
      throw new Error("No credentials available");
    }
    try {
      await credentials.getPromise();
    } catch (e: any) {
      // Check if this is a token verification error
      const errorMessage = e?.message || "";
      const isTokenError =
        e?.name === NotAuthorizedExceptionName ||
        errorMessage.includes("Invalid login token") ||
        errorMessage.includes("Couldn't verify signed token") ||
        errorMessage.includes("NotAuthorizedException");

      if (isTokenError) {
        // Token is invalid, try to refresh the session
        const cognitoUser = userPool.getCurrentUser();
        if (cognitoUser) {
          try {
            // Try to refresh the session using refresh token
            const refreshedSession = await asyncRefreshSession(cognitoUser);
            const refreshedCredentials = getCredentials(refreshedSession);
            await refreshedCredentials.getPromise();
            setCredentials(refreshedCredentials);
            return refreshedCredentials;
          } catch (refreshError: any) {
            // Refresh failed, force re-authentication
            console.warn("Token refresh failed, forcing re-authentication:", refreshError);
            // Clear stored identity ID to force new identity
            window.localStorage.removeItem(
              `aws.cognito.identity-id.${config.identityPoolId}`
            );
            cognitoUser.signOut();
            setCredentials(getCredentials());
            setUser(null);
            setState(AuthState.Unauthenticated);
            throw new NotAuthorizedException(
              "Session expired. Please sign in again."
            );
          }
        } else {
          // No user available, set to unauthenticated
          setCredentials(getCredentials());
          setUser(null);
          setState(AuthState.Unauthenticated);
          throw new NotAuthorizedException("Please sign in to continue.");
        }
      } else {
        // Other error, try to refresh credentials normally
        await doLogin(userPool.getCurrentUser());
      }
    }
    return credentials;
  }, [credentials, config, getCredentials, userPool]);

  const asyncAuthenticateUser = useCallback(
    async function asyncAuthenticateUser(
      cognitoUser: CognitoUser,
      username: string,
      password: string
    ): Promise<CognitoUserSession> {
      const authenticationDetails = new AuthenticationDetails({
        Username: username,
        Password: password,
      });
      return new Promise(function (resolve, reject) {
        cognitoUser.authenticateUser(authenticationDetails, {
          onSuccess: async (session) => {
            /**
             * This code was adapted from code meant to work with AWS Amplify. The purpose is so that
             * if an unauthenticated user has registers, they keep the same IdentityID as they had
             * before. To be completely honest I'm not really sure how it works, so I left the
             * original comment below but it references a lot of Amplify stuff.
             */
            /*
             * This call to GetCredentialsForIdentityCommand links the newly authenticated user with the anonymous identity in the Identity Pool.
             * When https://github.com/aws-amplify/amplify-js/blob/e11e938184fbb935374ca8f19b96365a7d6af7ce/packages/core/src/Credentials.ts#L354
             * is called further down the chain inside `baseOnSuccess`, it will return the same IdentityId instead of creating a new one.
             * Thus, the same value of UserId will be used for Pinpoint before and after registration.
             * In the case when the user is already registered and is signing in (i.e. they already have their account in User Pool linked to an Identity),
             * this call to GetCredentialsForIdentityCommand will not make any additional links. Instead it will mark the new Identity as disabled and
             * will merge it into the old one - https://docs.aws.amazon.com/cognito/latest/developerguide/authentication-flow.html (under Merging Identities).
             * There's also this thread that covers that https://forums.aws.amazon.com/thread.jspa?threadID=238042
             */
            if (session) {
              // Verify token is not expired before using it
              if (isSessionTokenExpired(session)) {
                reject(
                  new NotAuthorizedException(
                    "Session token expired. Please sign in again."
                  )
                );
                return;
              }

              const cognitoClient = new CognitoIdentityClient({
                region: config.region,
              });

              try {
                await cognitoClient.send(
                  new GetCredentialsForIdentityCommand({
                    IdentityId: credentials?.identityId,
                    Logins: {
                      [`cognito-idp.${config.region}.amazonaws.com/${config.userPoolId}`]:
                        session.getIdToken().getJwtToken(),
                    },
                  })
                );
              } catch (tokenError: any) {
                const errorMessage = tokenError?.message || "";
                if (
                  errorMessage.includes("Invalid login token") ||
                  errorMessage.includes("Couldn't verify signed token") ||
                  tokenError?.name === NotAuthorizedExceptionName
                ) {
                  reject(
                    new NotAuthorizedException(
                      "Invalid login token. Please sign in again."
                    )
                  );
                  return;
                }
                throw tokenError;
              }
            }
            resolve(session);
          },
          onFailure: reject,
          newPasswordRequired: reject,
        });
      });
    },
    [config, credentials]
  );

  /**
   * Used internally to get a user session and an identity ID
   */
  const doLogin = useCallback(
    async (cognitoUser: CognitoUser | null) => {
      try {
        if (!cognitoUser) {
          const unauthenticatedCredentials = getCredentials();
          await unauthenticatedCredentials.getPromise();
          setCredentials(unauthenticatedCredentials);
          setUser(null);
          setState(AuthState.Unauthenticated);
        } else {
          let session: CognitoUserSession;
          try {
            session = await asyncGetSession(cognitoUser);
          } catch (sessionError: any) {
            // If getting session fails, try refreshing it
            try {
              session = await asyncRefreshSession(cognitoUser);
            } catch (refreshError: any) {
              // Both getting and refreshing session failed
              const errorMessage = refreshError?.message || "";
              const isTokenError =
                refreshError?.name === NotAuthorizedExceptionName ||
                errorMessage.includes("Invalid login token") ||
                errorMessage.includes("Couldn't verify signed token") ||
                errorMessage.includes("NotAuthorizedException");

              if (isTokenError) {
                // Token is invalid, force re-authentication
                console.warn("Session invalid, forcing re-authentication");
                window.localStorage.removeItem(
                  `aws.cognito.identity-id.${config.identityPoolId}`
                );
                cognitoUser.signOut();
                setCredentials(getCredentials());
                setUser(null);
                setState(AuthState.Unauthenticated);
                return;
              }
              throw refreshError;
            }
          }

          // Check if token is expired before using it
          if (isSessionTokenExpired(session)) {
            try {
              // Try to refresh the session
              session = await asyncRefreshSession(cognitoUser);
            } catch (refreshError: any) {
              // Refresh failed, force re-authentication
              console.warn("Token expired and refresh failed, forcing re-authentication");
              window.localStorage.removeItem(
                `aws.cognito.identity-id.${config.identityPoolId}`
              );
              cognitoUser.signOut();
              setCredentials(getCredentials());
              setUser(null);
              setState(AuthState.Unauthenticated);
              return;
            }
          }

          let authenticatedCredentials = getCredentials(session);
          try {
            await authenticatedCredentials.getPromise();
          } catch (credError: any) {
            const errorMessage = credError?.message || "";
            const isTokenError =
              credError?.name === NotAuthorizedExceptionName ||
              errorMessage.includes("Invalid login token") ||
              errorMessage.includes("Couldn't verify signed token") ||
              errorMessage.includes("NotAuthorizedException");

            if (isTokenError) {
              // Token verification failed, try refreshing session again
              try {
                session = await asyncRefreshSession(cognitoUser);
                authenticatedCredentials = getCredentials(session);
                await authenticatedCredentials.getPromise();
              } catch (refreshError: any) {
                // Refresh failed, force re-authentication
                console.warn("Token verification failed and refresh failed, forcing re-authentication");
                window.localStorage.removeItem(
                  `aws.cognito.identity-id.${config.identityPoolId}`
                );
                cognitoUser.signOut();
                setCredentials(getCredentials());
                setUser(null);
                setState(AuthState.Unauthenticated);
                return;
              }
            } else {
              throw credError;
            }
          }

          const userData: UserData = await promisify(
            cognitoUser.getUserData,
            cognitoUser
          )();
          const attributes = userAttributesToObject<IUserAttributesObject>(
            userData.UserAttributes
          );
          // We need to set the user here
          setCredentials(authenticatedCredentials);

          setUser({
            ...attributes,
            email: attributes.email,
            isTblAccount: attributes.email.endsWith("@thunderbunnylabs.com"),
          });
          setState(AuthState.Authenticated);
        }
      } catch (e: any) {
        const errorMessage = e?.message || "";
        const isTokenError =
          e?.name === NotAuthorizedExceptionName ||
          errorMessage.includes("Invalid login token") ||
          errorMessage.includes("Couldn't verify signed token") ||
          errorMessage.includes("NotAuthorizedException");

        if (isTokenError) {
          // Token error, set to unauthenticated instead of error state
          console.warn("Authentication token error:", e);
          setUser(null);
          setState(AuthState.Unauthenticated);
        } else {
          Sentry.captureException(e);
          setUser(null);
          setState(AuthState.Error);
        }
      }
    },
    [getCredentials, config]
  );

  // Auto sign in
  useEffect(() => {
    async function autoLogin() {
      const cognitoUser = userPool.getCurrentUser();
      await doLogin(cognitoUser);
    }
    autoLogin();
  }, []);

  /**
   * Sign in an unauthenticated user
   */
  async function signIn(username: string, password: string): Promise<void> {
    const cognitoUser = new CognitoUser({
      Username: username,
      Pool: userPool,
    });

    try {
      await asyncAuthenticateUser(cognitoUser, username, password);
      await doLogin(cognitoUser);
    } catch (e) {
      if (e.name === UserNotConfirmedExceptionName) {
        throw new UserNotConfirmedException();
      } else if (e.name === PasswordResetRequiredExceptionName) {
        throw new PasswordResetRequiredException();
      } else {
        throw e;
      }
    }
  }

  /**
   * Sign out of the application and clear the cached user
   */
  async function signOut() {
    const cognitoUser = await userPool.getCurrentUser();
    if (cognitoUser) {
      // Needed to prevent this issue: https://github.com/aws-amplify/amplify-js/issues/5696
      window.localStorage.removeItem(
        `aws.cognito.identity-id.${config.identityPoolId}`
      );
      cognitoUser.signOut();
      await doLogin(userPool.getCurrentUser());
    }
  }

  /**
   * Returns the new user on success, and throws an error if they were not registered for any reason.
   */
  async function signUp(
    username: string,
    password: string,
    attributeList: CognitoUserAttribute[] = []
  ) {
    const result = await promisify(userPool.signUp, userPool)(
      username,
      password,
      attributeList,
      null
    );
    doLogin(result.user);
  }

  /**
   * Verify an email address for a new user
   */
  async function verifyEmail(
    email: string,
    password: string,
    verificationCode: string
  ) {
    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: userPool,
    });
    // The only non-error result here is a success message.
    await promisify(cognitoUser.confirmRegistration, cognitoUser)(
      verificationCode,
      true
    );
    if (password) {
      await asyncAuthenticateUser(cognitoUser, email, password);
      await doLogin(cognitoUser);
    }
  }

  /**
   * Re-sends the email verification code to a user.
   */
  async function resendEmailVerification(email: string): Promise<void> {
    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: userPool,
    });
    // The only non-error result here is a success message.
    await promisify(cognitoUser.resendConfirmationCode, cognitoUser)();
  }

  async function sendPasswordReset(email: string): Promise<void> {
    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: userPool,
    });
    // The only non-error result here is a success message.
    await asyncForgotPassword(cognitoUser);
  }

  async function resetPassword(
    email: string,
    verificationCode: string,
    newPassword: string
  ): Promise<void> {
    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: userPool,
    });
    // Throws error for any non-success state
    await asyncConfirmPassword(cognitoUser, verificationCode, newPassword);
    // Log in automatically
    await asyncAuthenticateUser(cognitoUser, email, newPassword);
    await doLogin(cognitoUser);
  }

  async function changePassword(
    oldPassword: string,
    newPassword: string
  ): Promise<void> {
    const cognitoUser = await userPool.getCurrentUser();
    await asyncGetSession(cognitoUser);
    await asyncChangePassword(cognitoUser, oldPassword, newPassword);
  }

  const authorizedFetch = useMemo(() => {
    return async (url: string, options: RequestInit = {}) => {
      const credentials = await currentCredentials();
      const fetch = createSigV4Fetch(credentials);
      return fetch(url, options);
    };
  }, [credentials, currentCredentials]);

  return (
    <Context.Provider
      value={{
        state,
        user,
        identityId: credentials?.identityId,
        isAuthenticated: state === AuthState.Authenticated,
        authorizedFetch,
        currentCredentials,
        signIn,
        signOut,
        signUp,
        resendEmailVerification,
        verifyEmail,
        sendPasswordReset,
        resetPassword,
        changePassword,
      }}
    >
      {children}
    </Context.Provider>
  );
}

/**
 * Checks if a JWT token is expired or will expire soon (within 60 seconds).
 */
function isTokenExpired(token: string): boolean {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    const exp = payload.exp * 1000; // Convert to milliseconds
    const now = Date.now();
    // Consider token expired if it expires within 60 seconds
    return exp - now < 60000;
  } catch (e) {
    // If we can't parse the token, consider it invalid/expired
    return true;
  }
}

/**
 * Checks if a Cognito session's ID token is expired or will expire soon.
 */
function isSessionTokenExpired(session: CognitoUserSession): boolean {
  try {
    const idToken = session.getIdToken();
    return isTokenExpired(idToken.getJwtToken());
  } catch (e) {
    return true;
  }
}

/**
 * Refreshes a Cognito user session using the refresh token.
 */
async function asyncRefreshSession(
  cognitoUser: CognitoUser
): Promise<CognitoUserSession> {
  return new Promise<CognitoUserSession>((resolve, reject) => {
    cognitoUser.getSession((err, session) => {
      if (err) {
        reject(err);
        return;
      }
      if (!session) {
        reject(new Error("No session available"));
        return;
      }
      const refreshToken = session.getRefreshToken();
      if (!refreshToken) {
        reject(new Error("No refresh token available"));
        return;
      }
      cognitoUser.refreshSession(refreshToken, (refreshErr, newSession) => {
        if (refreshErr) {
          reject(refreshErr);
        } else {
          resolve(newSession);
        }
      });
    });
  });
}

/**
 * Some helper methods to deal with the weird callback structure of Cognito methods.
 */
async function asyncGetSession(
  cognitoUser: CognitoUser
): Promise<CognitoUserSession> {
  return await promisify<CognitoUserSession>(
    cognitoUser.getSession,
    cognitoUser
  )();
}

async function asyncForgotPassword(cognitoUser: CognitoUser): Promise<any> {
  return new Promise(function (resolve, reject) {
    cognitoUser.forgotPassword({
      onSuccess: resolve,
      onFailure: reject,
    });
  });
}

async function asyncConfirmPassword(
  cognitoUser: CognitoUser,
  verificationCode: string,
  newPassword: string
): Promise<any> {
  return new Promise(function (resolve, reject) {
    cognitoUser.confirmPassword(verificationCode, newPassword, {
      onSuccess: resolve,
      onFailure: reject,
    });
  });
}

async function asyncChangePassword(
  cognitoUser: CognitoUser,
  oldPassword: string,
  newPassword: string
): Promise<any> {
  return new Promise<void>(function (resolve, reject) {
    cognitoUser.changePassword(oldPassword, newPassword, (error) => {
      if (error) {
        reject(error);
      } else {
        resolve();
      }
    });
  });
}

export function useAuthContext() {
  return useContext<AuthContext>(Context);
}
